import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {AutocompleteProps} from '@mui/material/Autocomplete';

export type AppMUIAutocompleteProps<
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
> = {
  label?: string;
  variant?: 'filled' | 'outlined' | 'standard';
  placeholder?: string;
  required?: boolean;
  styles?: React.CSSProperties;
  error?: boolean;
  helperText?: string;
  startAdornment?: React.ReactNode;
} & Omit<
  AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  'renderInput'
>;

function AppMUIAutocomplete<
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
>({
  label,
  variant = 'filled',
  placeholder,
  required = false,
  styles,
  error,
  helperText,
  startAdornment,
  sx,
  slotProps,
  ...rest
}: AppMUIAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) {
  const renderedLabel =
    required && label ? (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
        }}>
        <span style={{color: '#e52055'}}>*</span>
        {label}
      </span>
    ) : (
      label
    );

  return (
    <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
      disablePortal
      {...rest}
      sx={{
        '& + .MuiAutocomplete-popper .MuiAutocomplete-listbox > li:hover': {
          backgroundColor: '#D8FEFF',
        },
        '& + .MuiAutocomplete-popper .MuiAutocomplete-listbox > li[aria-selected="true"]:hover':
          {
            backgroundColor: '#D8FEFF',
          },
        ...sx,
      }}
      slotProps={{
        popper: {
          style: {
            border: '1px solid #707070',
            boxShadow: '0 4px 4px #00000040',
          },
          modifiers: [
            {
              name: 'flip',
              options: {
                boundary: 'viewport',
              },
            },
            {
              name: 'preventOverflow',
              options: {boundary: 'viewport'},
            },
          ],
        },

        ...slotProps,
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={renderedLabel}
          variant={variant}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          hiddenLabel={!label}
          sx={{
            ...styles,
            '& .MuiFilledInput-root': {
              backgroundColor: '#1D1B2014',
            },
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <>
                  {startAdornment}
                  {params.InputProps.startAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}

export default AppMUIAutocomplete;
